import React, { useReducer } from 'react';
import {
    MyCampaignResponse,
    ICampaignEditInfo,
    IPhotoInfo,
    ITeamMembers,
    IPerson,
    FundraiserListItem,
} from '../types/campaign';

interface Props {
    children: React.ReactNode;
}

interface WordpressPublishResponse {
    wordpress_id: number;
    wordpress_permalink: string;
}

export type CampaignAction =
    | {
          type: 'set_campaign';
          payload: MyCampaignResponse | null;
      }
    | {
          type: 'set_campaign_message';
          payload: string | null;
      }
    | {
          type: 'set_campaign_edit_info';
          payload: ICampaignEditInfo;
      }
    | {
          type: 'set_campaign_photo';
          payload: IPhotoInfo;
      }
    | {
          type: 'set_team_members';
          payload: ITeamMembers;
      }
    | {
          type: 'set_person';
          payload: IPerson;
      }
    | {
          type: 'set_patient';
          payload: any;
      }
    | {
          type: 'set_application';
          payload: any;
      }
    | {
          type: 'set_wordpress';
          payload: WordpressPublishResponse;
      }
    | {
          type: 'set_events';
          payload: FundraiserListItem[];
      }
    | {
          type: 'add_event';
          payload: any;
      }
    | {
          type: 'update_events';
          payload: any;
      }
    | {
          type: 'set_campaign_photo_url';
          payload: any;
      }
    | {
          type: 'remove_event';
          payload: any;
      }
    | {
          type: 'set_show_campaign_message';
          payload: boolean;
      }
    | {
          type: 'set_needs_photo_sync';
          payload: boolean;
      };

export interface CampaignState {
    campaign: MyCampaignResponse | null;
    campaignMessage: string | null;
    showCampaignMessage?: boolean;
    needsPhotoSync?: boolean;
}

const initialState: CampaignState = {
    campaign: null,
    campaignMessage: null,
    showCampaignMessage: true,
    needsPhotoSync: false,
};

export const CampaignContext = React.createContext<any | null>(null);

const CampaignReducer = (state: CampaignState, action: CampaignAction) => {
    let campaignDetail;
    switch (action.type) {
        case 'set_campaign':
            return {
                ...state,
                campaign: action.payload,
            };

        case 'set_campaign_edit_info':
            return {
                ...state,
                campaign: {
                    ...state.campaign,
                    campaignEditInfo: action.payload,
                },
            };

        case 'set_campaign_photo':
            return {
                ...state,
                campaign: {
                    ...state.campaign,
                    campaignPhotoInfo: action.payload,
                },
            };

        case 'set_campaign_photo_url':
            if (!state.campaign?.campaignPhotoInfo) {
                console.log('state.campaign: ', state.campaign);
                console.log('No campaign photo info');
                return state;
            }
            let newPhotoInfo = {
                ...state.campaign.campaignPhotoInfo,
                photo_url: action.payload,
                photo_thumb_url: action.payload,
            };
            return {
                ...state,
                campaign: {
                    ...state.campaign,
                    campaignPhotoInfo: newPhotoInfo,
                },
            };

        case 'set_team_members':
            campaignDetail = state.campaign?.campaignDetail;
            if (!campaignDetail) {
                return state;
            }
            campaignDetail.team_members = action.payload;
            return {
                ...state,
                campaign: {
                    ...state.campaign,
                    campaignDetail,
                },
            };

        case 'set_person':
            return {
                ...state,
                campaign: {
                    ...state.campaign,
                    person: action.payload,
                },
            };

        case 'set_patient':
            const stateCampaign = { ...state.campaign };
            const { consent, ...val } = action.payload;
            if (
                typeof consent === 'boolean' &&
                stateCampaign.campaignApplication
            ) {
                stateCampaign.campaignApplication.mp_give_consent_for_mp_to_provide_info =
                    consent;
            }

            return {
                ...state,
                campaign: {
                    ...stateCampaign,
                    patient: { ...val },
                },
            };

        case 'set_application':
            return {
                ...state,
                campaign: {
                    ...state.campaign,
                    campaignApplication: action.payload,
                },
            };

        case 'set_events': {
            const { campaign } = state;
            return {
                ...state,
                campaign: {
                    ...campaign,
                    campaignFundraisers: action.payload,
                },
            };
        }

        case 'add_event': {
            const newFundraiser = action.payload;
            const { campaign } = state;
            const campaignFundraisers = campaign?.campaignFundraisers || [];
            const nextFundraisers = [...campaignFundraisers];
            nextFundraisers.push(newFundraiser);
            return {
                ...state,
                campaign: {
                    ...campaign,
                    campaignFundraisers: nextFundraisers,
                },
            };
        }

        case 'remove_event': {
            const id = action.payload;
            const { campaign } = state;
            const campaignFundraisers = campaign?.campaignFundraisers || [];
            const nextFundraisers = campaignFundraisers.filter(
                (f: any) => f.id !== id
            );
            return {
                ...state,
                campaign: {
                    ...campaign,
                    campaignFundraisers: nextFundraisers,
                },
            };
        }

        case 'update_events': {
            const editedFundraiser = action.payload;
            const { campaign } = state;
            const campaignFundraisers = campaign?.campaignFundraisers || [];
            const nextFundraisers = campaignFundraisers.map((fundraiser) => {
                if (fundraiser.id === editedFundraiser?.id) {
                    return editedFundraiser;
                } else {
                    return fundraiser;
                }
            });
            return {
                ...state,
                campaign: {
                    ...campaign,
                    campaignFundraisers: nextFundraisers,
                },
            };
        }

        case 'set_wordpress':
            campaignDetail = state.campaign?.campaignDetail;
            if (!campaignDetail) {
                return state;
            }
            const { wordpress_id, wordpress_permalink } = action.payload;
            campaignDetail.wordpress_id = wordpress_id;
            campaignDetail.wordpress_permalink = wordpress_permalink;
            return {
                ...state,
                campaign: {
                    ...state.campaign,
                    campaignDetail,
                },
            };

        case 'set_show_campaign_message':
            return {
                ...state,
                showCampaignMessage: action.payload,
            };

        case 'set_needs_photo_sync':
            return {
                ...state,
                needsPhotoSync: action.payload,
            };

        default:
            return state;
    }
};

export const CampaignContextProvider: React.FC<Props> = ({ children }) => {
    const [state, dispatch] = useReducer(CampaignReducer, initialState);

    return (
        <CampaignContext.Provider value={[state, dispatch]}>
            {children}
        </CampaignContext.Provider>
    );
};
